import React, { ReactNode, createContext, useContext, useState } from 'react';

import { MentionProps, VariableProps } from './types/rich-text-editor';

interface RichTextEditorContextType {
	mentionProps: MentionProps;
	editable: boolean;
	setMentionProps: (props: MentionProps) => void;
	variableProps: VariableProps;
	setVariableProps: (props: VariableProps) => void;
	getVariableTooltipFromToken: (token: string) => string | null;
	showVariableBrackets: boolean;
	variableMentionStyle: boolean;
}

const RichTextEditorContext = createContext<RichTextEditorContextType>(
	{} as RichTextEditorContextType
);

interface RichTextEditorProviderProps {
	children: ReactNode;
	editMode?: boolean;
}

export const RichTextEditorProvider: React.FC<RichTextEditorProviderProps> = ({
	children,
	editMode,
}) => {
	const [mentionProps, setMentionProps] = useState<MentionProps>({
		items: [],
	});
	const [variableProps, setVariableProps] = useState<VariableProps>({
		items: [],
	});

	const getVariableTooltipFromToken = (token: string) => {
		const variable = variableProps.items.find((item) => item.token === token);

		// A pill can outlive the list that offered it — a link template loaded into a plain composer.
		if (!variable) return null;

		return variableProps.getTooltip?.(variable) || variable.description || null;
	};

	return (
		<RichTextEditorContext.Provider
			value={{
				mentionProps,
				editable: editMode ?? true,
				setMentionProps,
				variableProps,
				setVariableProps,
				getVariableTooltipFromToken,
				showVariableBrackets: variableProps.showBrackets ?? true,
				variableMentionStyle: variableProps.mentionStyle ?? false,
			}}
		>
			{children}
		</RichTextEditorContext.Provider>
	);
};

export const useRichTextEditor = () => {
	const context = useContext(RichTextEditorContext);
	if (context === undefined) {
		throw new Error('useRichTextEditor must be used within a RichTextEditorProvider');
	}
	return context;
};
